import { supabase } from "@/integrations/supabase/client";
import { 
  CommunityPost,
  CommunityPostComment,
  CreatePostData,
  UpdatePostData,
  CreateCommentData,
  UpdateCommentData,
  PostFilters,
  PostListResponse,
  CommentFilters,
  CommentListResponse,
  UserPostInteractions,
  UserCommentInteractions,
  PostPermissions,
  PostEngagementStats
} from "@/types/community";

// Função para limpar e validar o conteúdo HTML
const cleanAndValidateContent = (content: string): string => {
  if (!content || typeof content !== 'string') {
    throw new Error('Conteúdo não pode estar vazio');
  }

  console.log('🧹 [cleanAndValidateContent] === INÍCIO DA VALIDAÇÃO ===');
  console.log('🧹 [cleanAndValidateContent] Conteúdo original:', content);
  console.log('🧹 [cleanAndValidateContent] Tamanho original:', content.length);

  // Detectar imagens base64
  const base64ImageRegex = /<img[^>]+src\s*=\s*["']data:image\/[^"']+["'][^>]*>/gi;
  const base64Images = content.match(base64ImageRegex);
  
  if (base64Images && base64Images.length > 0) {
    console.error('❌ [cleanAndValidateContent] Detectadas imagens base64:', base64Images.length);
    throw new Error('Por favor, use URLs de imagens ao invés de colar imagens diretamente. Cole a URL da imagem no campo "Imagem (Opcional)" do formulário.');
  }

  // Remove tags HTML para verificar se há texto real
  const textOnly = content.replace(/<[^>]*>/g, '').trim();
  
  // Remove entidades HTML comuns que podem restar
  const cleanText = textOnly
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .trim();

  console.log('🧹 [cleanAndValidateContent] Texto sem HTML:', textOnly);
  console.log('🧹 [cleanAndValidateContent] Texto limpo final:', cleanText);
  console.log('🧹 [cleanAndValidateContent] Tamanho do texto limpo:', cleanText.length);

  // Verificar se há texto real (pelo menos 1 caractere)
  if (!cleanText || cleanText.length === 0) {
    console.error('❌ [cleanAndValidateContent] Erro: Texto limpo está vazio');
    throw new Error('O conteúdo não pode estar vazio');
  }

  // Verificar o tamanho do HTML original (não o texto limpo)
  const trimmedContent = content.trim();
  console.log('🧹 [cleanAndValidateContent] HTML trimmed:', trimmedContent);
  console.log('🧹 [cleanAndValidateContent] Tamanho do HTML trimmed:', trimmedContent.length);

  if (trimmedContent.length > 5000) {
    console.error('❌ [cleanAndValidateContent] Erro: HTML muito grande');
    throw new Error(`O conteúdo HTML é muito longo (${trimmedContent.length} caracteres). Máximo permitido: 5000 caracteres. Isso geralmente acontece quando imagens são coladas diretamente no editor.`);
  }

  console.log('✅ [cleanAndValidateContent] Validação passou! Retornando HTML original');
  console.log('🧹 [cleanAndValidateContent] === FIM DA VALIDAÇÃO ===');

  // Retorna o conteúdo original HTML se passou na validação
  return trimmedContent;
};

export interface CreateCommunityPostData {
  title: string;
  content: string;
  community_id: string;
  author_id: string;
  category_id?: string;
  media_url?: string;
  post_type?: 'text' | 'image' | 'video' | 'file';
  is_pinned?: boolean;
}

export interface CommunityPost {
  id: string;
  title: string;
  content: string;
  community_id: string;
  author_id: string;
  category_id?: string;
  post_type: string;
  is_pinned: boolean;
  likes_count: number;
  comments_count: number;
  created_at: string;
  updated_at: string;
  author?: {
    id: string;
    nickname: string;
    avatar_url?: string;
  };
}

// ===============================
// LISTAGEM DE POSTS
// ===============================

export const fetchCommunityPosts = async (filters: PostFilters = {}): Promise<PostListResponse> => {
  try {
    const {
      community_id,
      author_id,
      category_id,
      is_pinned,
      search,
      limit = 10,
      offset = 0
    } = filters;

    console.log('📋 [fetchCommunityPosts] Buscando posts com filtros:', filters);

    // Primeiro verificar se a tabela existe
    console.log('🔍 [fetchCommunityPosts] Verificando existência da tabela...');
    const { data: tableCheck, error: tableError } = await supabase
      .from('community_posts')
      .select('id')
      .limit(1);

    if (tableError) {
      console.error('❌ [fetchCommunityPosts] Tabela community_posts não existe ou erro de acesso:', tableError);
      // Retornar array vazio se tabela não existe
      return {
        posts: [],
        total_count: 0,
        has_more: false
      };
    }

    console.log('✅ [fetchCommunityPosts] Tabela existe, continuando...');

    // Query simplificada - primeiro vamos buscar apenas os posts básicos
    let query = supabase
      .from('community_posts')
      .select('*')
      .order('is_pinned', { ascending: false })
      .order('created_at', { ascending: false });

    // Filtros
    if (community_id) {
      query = query.eq('community_id', community_id);
    }

    if (author_id) {
      query = query.eq('author_id', author_id);
    }

    // Temporariamente desabilitado
    /*
    if (category_id) {
      query = query.eq('category_id', category_id);
    }
    */

    if (typeof is_pinned === 'boolean') {
      query = query.eq('is_pinned', is_pinned);
    }

    if (search) {
      query = query.or(`content.ilike.%${search}%`);
    }

    // Contagem total
    const countQuery = supabase
      .from('community_posts')
      .select('*', { count: 'exact', head: true });

    if (community_id) countQuery.eq('community_id', community_id);
    if (author_id) countQuery.eq('author_id', author_id);
    // if (category_id) countQuery.eq('category_id', category_id); // Temporariamente desabilitado
    if (typeof is_pinned === 'boolean') countQuery.eq('is_pinned', is_pinned);
    if (search) countQuery.or(`content.ilike.%${search}%`);

    console.log('🔍 [fetchCommunityPosts] Executando queries...');

    const [{ data: posts, error }, { count, error: countError }] = await Promise.all([
      query.range(offset, offset + limit - 1),
      countQuery
    ]);

    if (error) {
      console.error('❌ [fetchCommunityPosts] Erro na query:', error);
      console.error('❌ [fetchCommunityPosts] Detalhes do erro:', {
        code: error.code,
        message: error.message,
        details: error.details,
        hint: error.hint
      });
      throw error;
    }
    if (countError) {
      console.error('❌ [fetchCommunityPosts] Erro no count:', countError);
      throw countError;
    }

    const total_count = count || 0;
    const has_more = offset + limit < total_count;

    console.log('✅ [fetchCommunityPosts] Posts encontrados:', posts?.length || 0);
    console.log('📊 [fetchCommunityPosts] Total count:', total_count);

    // Agora vamos buscar os dados dos autores e categorias para os posts encontrados
    let postsWithDetails = posts || [];
    
    if (posts && posts.length > 0) {
      console.log('👥 [fetchCommunityPosts] Buscando dados dos autores e categorias...');
      
      // Buscar dados dos autores
      const authorIds = [...new Set(posts.map(post => post.author_id))];
      console.log('🔍 [fetchCommunityPosts] IDs dos autores para buscar:', authorIds);
      
      let authors: any[] = [];
      
      if (authorIds.length > 0) {
        console.log('🔍 [fetchCommunityPosts] Buscando autores individualmente...');
        
        // Buscar autores um por um para evitar erro na query .in()
        for (const authorId of authorIds) {
          try {
            console.log('👤 [fetchCommunityPosts] Buscando autor:', authorId);
            
            // Primeiro buscar dados básicos do profile
            const { data: profileData, error: profileError } = await supabase
              .from('profiles')
              .select('id, nickname, role, slug')
              .eq('id', authorId)
              .single();

            if (profileError) {
              console.warn('⚠️ [fetchCommunityPosts] Erro ao buscar perfil:', authorId, profileError);
              continue;
            }

            console.log('✅ [fetchCommunityPosts] Perfil encontrado:', profileData);

            // Agora buscar a imagem baseada no role
            let avatar_url = null;
            
            if (profileData.role === 'mentor') {
              console.log('🖼️ [fetchCommunityPosts] Buscando imagem do mentor...');
              
              const { data: mentorData, error: mentorError } = await supabase
                .from('mentor_info')
                .select('image_url')
                .eq('profile_id', authorId)
                .maybeSingle();
              
              if (!mentorError && mentorData?.image_url) {
                // Processar URL da imagem
                if (mentorData.image_url.startsWith('http')) {
                  avatar_url = mentorData.image_url;
                } else {
                  // Se é apenas o nome do arquivo, formar caminho completo: {user_id}/{filename}
                  let fullPath = mentorData.image_url;
                  
                  // Se não contém '/', formar o caminho completo
                  if (!mentorData.image_url.includes('/')) {
                    fullPath = `${authorId}/${mentorData.image_url}`;
                  }
                  
                  const { data: urlData } = supabase.storage
                    .from('profile-images')
                    .getPublicUrl(fullPath);
                  avatar_url = urlData.publicUrl;
                }
                console.log('✅ [fetchCommunityPosts] Imagem do mentor encontrada:', avatar_url);
              }
            } else if (profileData.role === 'jogador') {
              console.log('🖼️ [fetchCommunityPosts] Buscando imagem do jogador...');
              
              const { data: playerData, error: playerError } = await supabase
                .from('player_social_media')
                .select('image_url')
                .eq('profile_id', authorId)
                .order('created_at', { ascending: false })
                .limit(1)
                .maybeSingle();
              
              if (!playerError && playerData?.image_url) {
                // Processar URL da imagem
                if (playerData.image_url.startsWith('http')) {
                  avatar_url = playerData.image_url;
                } else {
                  // Se é apenas o nome do arquivo, formar caminho completo: {user_id}/{filename}
                  let fullPath = playerData.image_url;
                  
                  // Se não contém '/', formar o caminho completo
                  if (!playerData.image_url.includes('/')) {
                    fullPath = `${authorId}/${playerData.image_url}`;
                  }
                  
                  const { data: urlData } = supabase.storage
                    .from('profile-images')
                    .getPublicUrl(fullPath);
                  avatar_url = urlData.publicUrl;
                }
                console.log('✅ [fetchCommunityPosts] Imagem do jogador encontrada:', avatar_url);
              }
            }

            // Adicionar autor com imagem encontrada
            const authorWithImage = {
              ...profileData,
              avatar_url
            };
            
            authors.push(authorWithImage);
            console.log('✅ [fetchCommunityPosts] Autor completo:', {
              id: authorWithImage.id,
              nickname: authorWithImage.nickname,
              avatar_url: authorWithImage.avatar_url
            });

          } catch (error) {
            console.warn('⚠️ [fetchCommunityPosts] Erro inesperado ao buscar autor:', authorId, error);
          }
        }
      } else {
        console.warn('⚠️ [fetchCommunityPosts] Nenhum ID de autor para buscar');
      }

      // Buscar dados das categorias - temporariamente desabilitado
      const categoryIds = [...new Set(posts.map(post => post.category_id).filter(Boolean))];
      let categories: any[] = [];
      
      // Temporariamente desabilitado para evitar erro 404
      /*
      if (categoryIds.length > 0) {
        const { data: categoriesData, error: categoryError } = await supabase
          .from('community_post_categories')
          .select('*')
          .in('id', categoryIds);

        if (categoryError) {
          console.warn('⚠️ [fetchCommunityPosts] Erro ao buscar categorias:', categoryError);
        } else {
          categories = categoriesData || [];
        }
      }
      */

      console.log('✅ [fetchCommunityPosts] Autores encontrados:', authors?.length || 0);
      console.log('✅ [fetchCommunityPosts] Categorias encontradas:', categories?.length || 0);
      
      // Mapear autores e categorias para os posts
      postsWithDetails = posts.map(post => {
        const foundAuthor = authors?.find(author => author.id === post.author_id);
        console.log('🔍 [fetchCommunityPosts] Mapeando autor para post:', post.id, {
          author_id: post.author_id,
          foundAuthor,
          allAuthors: authors
        });
        
        return {
          ...post,
          author: foundAuthor || {
            id: post.author_id,
            nickname: 'Usuário Desconhecido',
            avatar_url: null,
            slug: null,
            role: null
          },
          // category: post.category_id ? categories.find(cat => cat.id === post.category_id) : undefined // Temporariamente desabilitado
        };
      });
      
      // Buscar contagem de likes e comentários para cada post
      console.log('📊 [fetchCommunityPosts] Calculando likes e comentários...');
      const postsWithCounts = await Promise.all(
        postsWithDetails.map(async (post) => {
          try {
            // Contar likes
            const { data: likesData, error: likesError } = await supabase
              .from('community_post_likes')
              .select('id')
              .eq('post_id', post.id);
            
            const likes_count = likesError ? 0 : (likesData?.length || 0);
            
            // Contar comentários
            const { data: commentsData, error: commentsError } = await supabase
              .from('community_post_comments')
              .select('id')
              .eq('post_id', post.id);
            
            const comments_count = commentsError ? 0 : (commentsData?.length || 0);
            
            console.log(`📊 [fetchCommunityPosts] Post ${post.id}: ${likes_count} likes, ${comments_count} comentários`);
            
            return {
              ...post,
              likes_count,
              comments_count
            };
          } catch (error) {
            console.warn(`⚠️ [fetchCommunityPosts] Erro ao contar interações do post ${post.id}:`, error);
            return {
              ...post,
              likes_count: 0,
              comments_count: 0
            };
          }
        })
      );
      
      postsWithDetails = postsWithCounts;
    }

    return {
      posts: postsWithDetails as CommunityPost[],
      total_count,
      has_more
    };

  } catch (error) {
    console.error('❌ [fetchCommunityPosts] Erro ao buscar posts da comunidade:', error);
    throw new Error('Falha ao carregar posts');
  }
};

// ===============================
// BUSCAR POST INDIVIDUAL
// ===============================

export const fetchPostById = async (postId: string): Promise<CommunityPost | null> => {
  try {
    const { data: post, error } = await supabase
      .from('community_posts')
      .select(`
        *,
        author:profiles!author_id(id, nickname, avatar_url, slug),
        community:communities!community_id(id, name, slug)
      `)
      .eq('id', postId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        return null;
      }
      throw error;
    }

    return post as CommunityPost;

  } catch (error) {
    console.error('Erro ao buscar post:', error);
    throw new Error('Falha ao carregar post');
  }
};

// ===============================
// CRIAR POST NA COMUNIDADE
// ===============================

export const createCommunityPost = async (postData: CreateCommunityPostData): Promise<CommunityPost> => {
  try {
    console.log('📝 [createCommunityPost] Iniciando criação de post:', postData);

    // Verificar se o usuário é membro da comunidade ou dono
    console.log('🔍 [createCommunityPost] Verificando comunidade:', postData.community_id);
    const { data: communityData, error: communityError } = await supabase
      .from('communities')
      .select('*')
      .eq('id', postData.community_id)
      .single();

    if (communityError) {
      console.error('❌ [createCommunityPost] Erro ao buscar comunidade:', communityError);
      throw new Error('Comunidade não encontrada');
    }

    console.log('✅ [createCommunityPost] Comunidade encontrada:', communityData);

    // Verificar se o usuário tem permissão para postar
    const isOwner = communityData.mentor_id === postData.author_id;
    const isMember = true; // Por enquanto, permitir qualquer usuário logado
    
    if (!isOwner && !isMember) {
      throw new Error('Você não tem permissão para postar nesta comunidade');
    }

    console.log('✅ [createCommunityPost] Usuário tem permissão para postar');

    // Validar e limpar o conteúdo antes de criar o payload
    let validatedContent;
    try {
      validatedContent = cleanAndValidateContent(postData.content);
    } catch (validationError: any) {
      console.error('❌ [createCommunityPost] Erro na validação:', validationError.message);
      throw validationError;
    }
    
    // Criar payload simplificado
    const payload = {
      community_id: postData.community_id,
      author_id: postData.author_id,
      content: validatedContent,
      ...(postData.title && postData.title.trim() && { title: postData.title.trim() }),
      ...(postData.category_id && { category_id: postData.category_id }),
      ...(postData.media_url && { media_url: postData.media_url }),
      is_pinned: postData.is_pinned || false,
      created_at: new Date().toISOString()
    };

    console.log('📋 [createCommunityPost] Payload para inserção:', payload);

    // Inserir o post sem embed para evitar erro de relacionamento
    const { data: newPost, error: insertError } = await supabase
      .from('community_posts')
      .insert(payload)
      .select('*')
      .single();

    if (insertError) {
      console.error('❌ [createCommunityPost] Erro ao inserir post:', insertError);
      console.error('❌ [createCommunityPost] Código:', insertError.code);
      console.error('❌ [createCommunityPost] Mensagem:', insertError.message);
      console.error('❌ [createCommunityPost] Detalhes:', insertError.details);
      
      if (insertError.code === '23503') {
        throw new Error('Dados inválidos fornecidos');
      } else if (insertError.code === '42P01') {
        throw new Error('Tabela de posts não existe');
      } else if (insertError.code === '23514') {
        // Check constraint violation
        if (insertError.message.includes('community_posts_content_check')) {
          throw new Error('O conteúdo do post deve ter entre 1 e 5000 caracteres');
        }
        throw new Error('Dados fornecidos não atendem aos requisitos');
      } else {
        throw new Error(`Erro ao criar post: ${insertError.message}`);
      }
    }

    console.log('✅ [createCommunityPost] Post criado com sucesso:', newPost);

    // Buscar dados do autor separadamente (com imagem correta)
    const { data: authorProfile, error: authorError } = await supabase
      .from('profiles')
      .select('id, nickname, role, slug')
      .eq('id', postData.author_id)
      .single();

    let author = {
      id: postData.author_id,
      nickname: 'Usuário Desconhecido',
      avatar_url: null,
      slug: null,
      role: null
    };

    if (!authorError && authorProfile) {
      let avatar_url = null;
      
      // Buscar imagem baseada no role
      if (authorProfile.role === 'mentor') {
        console.log('🖼️ [createCommunityPost] Buscando imagem do mentor...');
        
        const { data: mentorData } = await supabase
          .from('mentor_info')
          .select('image_url')
          .eq('profile_id', authorProfile.id)
          .maybeSingle();
        
        // Processar URL da imagem
        if (mentorData?.image_url) {
          if (mentorData.image_url.startsWith('http')) {
            avatar_url = mentorData.image_url;
          } else {
            // Se é apenas o nome do arquivo, formar caminho completo: {user_id}/{filename}
            let fullPath = mentorData.image_url;
            
            // Se não contém '/', formar o caminho completo
            if (!mentorData.image_url.includes('/')) {
              fullPath = `${authorProfile.id}/${mentorData.image_url}`;
            }
            
            const { data: urlData } = supabase.storage
              .from('profile-images')
              .getPublicUrl(fullPath);
            avatar_url = urlData.publicUrl;
          }
        }
      } else if (authorProfile.role === 'jogador') {
        console.log('🖼️ [createCommunityPost] Buscando imagem do jogador...');
        
        const { data: playerData } = await supabase
          .from('player_social_media')
          .select('image_url')
          .eq('profile_id', authorProfile.id)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();
        
        // Processar URL da imagem
        if (playerData?.image_url) {
          if (playerData.image_url.startsWith('http')) {
            avatar_url = playerData.image_url;
          } else {
            // Se é apenas o nome do arquivo, formar caminho completo: {user_id}/{filename}
            let fullPath = playerData.image_url;
            
            // Se não contém '/', formar o caminho completo
            if (!playerData.image_url.includes('/')) {
              fullPath = `${authorProfile.id}/${playerData.image_url}`;
            }
            
            const { data: urlData } = supabase.storage
              .from('profile-images')
              .getPublicUrl(fullPath);
            avatar_url = urlData.publicUrl;
          }
        }
      }

      author = {
        ...authorProfile,
        avatar_url
      };
    }

    // Retornar o post com dados do autor corretos
    return {
      ...newPost,
      author,
      likes_count: 0,
      comments_count: 0
    };

  } catch (error) {
    console.error('❌ [createCommunityPost] Erro geral:', error);
    throw error;
  }
};

// ===============================
// ATUALIZAR POST
// ===============================

export const updatePost = async (postId: string, data: UpdatePostData): Promise<CommunityPost> => {
  try {
    console.log('✏️ [updatePost] Atualizando post:', postId, data);
    
    // Criar objeto de atualização, mapeando campos se necessário
    const updateData: any = {};
    
    if (data.title !== undefined) {
      updateData.title = data.title;
    }
    
    if (data.content !== undefined) {
      updateData.content = data.content;
    }
    
    // Mapear image_url para media_url
    if (data.image_url !== undefined) {
      updateData.media_url = data.image_url;
    }
    
    if (data.category_id !== undefined) {
      updateData.category_id = data.category_id;
    }
    
    if (data.is_pinned !== undefined) {
      updateData.is_pinned = data.is_pinned;
    }
    
    console.log('📝 [updatePost] Dados para atualização:', updateData);

    // Primeiro, atualizar apenas os dados básicos
    const { data: updatedPost, error } = await supabase
      .from('community_posts')
      .update(updateData)
      .eq('id', postId)
      .select('*')
      .single();

    if (error) {
      console.error('❌ [updatePost] Erro na atualização:', error);
      console.error('❌ [updatePost] Detalhes do erro:', {
        code: error.code,
        message: error.message,
        details: error.details,
        hint: error.hint
      });
      throw error;
    }
    
    console.log('✅ [updatePost] Post atualizado no banco:', updatedPost);

    // Buscar dados do autor separadamente (com imagem correta)
    const { data: authorProfile, error: authorError } = await supabase
      .from('profiles')
      .select('id, nickname, role, slug')
      .eq('id', updatedPost.author_id)
      .single();

    let author = {
      id: updatedPost.author_id,
      nickname: 'Usuário Desconhecido',
      avatar_url: null,
      slug: null,
      role: null
    };

    if (!authorError && authorProfile) {
      let avatar_url = null;
      
      // Buscar imagem baseada no role
      if (authorProfile.role === 'mentor') {
        console.log('🖼️ [updatePost] Buscando imagem do mentor...');
        const { data: mentorData } = await supabase
          .from('mentor_info')
          .select('image_url')
          .eq('profile_id', authorProfile.id)
          .maybeSingle();
        avatar_url = mentorData?.image_url || null;
      } else if (authorProfile.role === 'jogador') {
        console.log('🖼️ [updatePost] Buscando imagem do jogador...');
        const { data: playerData } = await supabase
          .from('player_social_media')
          .select('image_url')
          .eq('profile_id', authorProfile.id)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();
        avatar_url = playerData?.image_url || null;
      }

      author = {
        ...authorProfile,
        avatar_url
      };
    }

    // Retornar o post com dados do autor corretos
    const finalPost = {
      ...updatedPost,
      author,
      media_url: updatedPost.media_url
    };
    
    console.log('✅ [updatePost] Post final com autor correto:', finalPost);

    return finalPost as CommunityPost;

  } catch (error) {
    console.error('❌ [updatePost] Erro ao atualizar post:', error);
    throw new Error('Falha ao atualizar post');
  }
};

// ===============================
// DELETAR POST
// ===============================

export const deleteCommunityPost = async (postId: string, userId: string): Promise<void> => {
  console.log('🗑️ [deleteCommunityPost] Deletando post:', postId, 'por usuário:', userId);
  
  try {
    // Verificar se o post existe
    const { data: post, error: fetchError } = await supabase
      .from('community_posts')
      .select('id, author_id, community_id')
      .eq('id', postId)
      .single();

    if (fetchError || !post) {
      console.error('❌ [deleteCommunityPost] Post não encontrado:', fetchError);
      throw new Error('Post não encontrado');
    }

    console.log('✅ [deleteCommunityPost] Post encontrado:', post);

    // Buscar informações da comunidade separadamente
    const { data: community, error: communityError } = await supabase
      .from('communities')
      .select('mentor_id, owner_id')
      .eq('id', post.community_id)
      .single();

    if (communityError || !community) {
      console.error('❌ [deleteCommunityPost] Comunidade não encontrada:', communityError);
      throw new Error('Comunidade não encontrada');
    }

    console.log('✅ [deleteCommunityPost] Comunidade encontrada:', community);

    // Verificar permissões
    const canDelete = post.author_id === userId || 
                     community.mentor_id === userId || 
                     community.owner_id === userId;

    console.log('🔍 [deleteCommunityPost] Verificação de permissões:', {
      userId,
      authorId: post.author_id,
      mentorId: community.mentor_id,
      ownerId: community.owner_id,
      canDelete
    });

    if (!canDelete) {
      console.error('❌ [deleteCommunityPost] Usuário não tem permissão para deletar');
      throw new Error('Você não tem permissão para deletar este post');
    }

    // Deletar comentários primeiro
    const { error: commentsError } = await supabase
      .from('community_post_comments')
      .delete()
      .eq('post_id', postId);

    if (commentsError) {
      console.error('❌ [deleteCommunityPost] Erro ao deletar comentários:', commentsError);
    }

    // Deletar likes
    const { error: likesError } = await supabase
      .from('community_post_likes')
      .delete()
      .eq('post_id', postId);

    if (likesError) {
      console.error('❌ [deleteCommunityPost] Erro ao deletar likes:', likesError);
    }

    // Deletar o post
    const { error: deleteError } = await supabase
      .from('community_posts')
      .delete()
      .eq('id', postId);

    if (deleteError) {
      console.error('❌ [deleteCommunityPost] Erro ao deletar post:', deleteError);
      throw new Error('Falha ao deletar post');
    }

    console.log('✅ [deleteCommunityPost] Post deletado com sucesso');

  } catch (error) {
    console.error('❌ [deleteCommunityPost] Erro geral:', error);
    throw error;
  }
};

// ===============================
// CURTIR/DESCURTIR POST
// ===============================

export const togglePostLike = async (postId: string, userId: string): Promise<boolean> => {
  try {
    console.log('❤️ [togglePostLike] Tentando toggle like:', { postId, userId });
    
    // Verificar se o usuário está autenticado
    const { data: { user: currentUser }, error: authError } = await supabase.auth.getUser();
    console.log('🔐 [togglePostLike] Usuário autenticado:', { userId: currentUser?.id, authError });
    
    if (!currentUser) {
      throw new Error('Usuário não autenticado');
    }

    // Verificar se o usuário já curtiu o post usando community_post_likes
    const { data: existingLike, error: checkError } = await supabase
      .from('community_post_likes')
      .select('id')
      .eq('post_id', postId)
      .eq('user_id', userId)
      .maybeSingle();

    console.log('🔍 [togglePostLike] Resultado da verificação:', { existingLike, checkError });

    if (checkError) {
      console.error('❌ [togglePostLike] Erro ao verificar like existente:', checkError);
      throw checkError;
    }

    if (existingLike) {
      // Usuário já curtiu, remover like
      console.log('❤️ [togglePostLike] Removendo like existente...', existingLike.id);
      
      const { error: deleteError } = await supabase
        .from('community_post_likes')
        .delete()
        .eq('id', existingLike.id);

      if (deleteError) {
        console.error('❌ [togglePostLike] Erro ao remover like:', deleteError);
        throw deleteError;
      }

      console.log('✅ [togglePostLike] Like removido com sucesso');
      
      // Verificar se foi realmente removido
      const { data: verifyDelete, error: verifyDeleteError } = await supabase
        .from('community_post_likes')
        .select('*')
        .eq('post_id', postId)
        .eq('user_id', userId);
      
      console.log('🔍 [togglePostLike] Verificação após remoção:', { verifyDelete, verifyDeleteError });
      
      return false; // Não curtido mais
    } else {
      // Usuário não curtiu ainda, adicionar like
      console.log('❤️ [togglePostLike] Adicionando novo like...');
      
      const insertData = {
        post_id: postId,
        user_id: userId,
        created_at: new Date().toISOString()
      };
      
      console.log('📝 [togglePostLike] Dados para inserção:', insertData);
      console.log('📝 [togglePostLike] Usuário autenticado ID:', currentUser.id);
      console.log('📝 [togglePostLike] Match IDs?', userId === currentUser.id);
      
      const { data: insertResult, error: insertError } = await supabase
        .from('community_post_likes')
        .insert(insertData)
        .select('*');

      console.log('📝 [togglePostLike] Resultado da inserção:', { insertResult, insertError });

      if (insertError) {
        console.error('❌ [togglePostLike] Erro ao adicionar like:', insertError);
        console.error('❌ [togglePostLike] Detalhes do erro:', {
          code: insertError.code,
          message: insertError.message,
          details: insertError.details,
          hint: insertError.hint
        });
        throw insertError;
      }

      console.log('✅ [togglePostLike] Like adicionado com sucesso:', insertResult);
      
      // Verificar se foi realmente inserido (aguardar um pouco para a transação)
      await new Promise(resolve => setTimeout(resolve, 500));
      
      const { data: verifyInsert, error: verifyInsertError } = await supabase
        .from('community_post_likes')
        .select('*')
        .eq('post_id', postId)
        .eq('user_id', userId);
      
      console.log('🔍 [togglePostLike] Verificação após inserção:', { verifyInsert, verifyInsertError });
      
      // Verificar total de likes para este post
      const { data: allLikesForPost, error: allLikesError } = await supabase
        .from('community_post_likes')
        .select('*')
        .eq('post_id', postId);
      
      console.log('🔍 [togglePostLike] Todos os likes para o post:', { total: allLikesForPost?.length, allLikesForPost, allLikesError });
      
      return true; // Curtido
    }
  } catch (error) {
    console.error('❌ [togglePostLike] Erro geral:', error);
    throw error;
  }
};

// ===============================
// BUSCAR INTERAÇÕES DO USUÁRIO
// ===============================

export const fetchUserPostInteractions = async (postId: string): Promise<{ liked: boolean }> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      return { liked: false };
    }

    console.log('🔍 [fetchUserPostInteractions] Verificando interações:', { postId, userId: user.id });

    // Verificar se o usuário curtiu o post usando community_post_likes
    const { data: likeData, error: likeError } = await supabase
      .from('community_post_likes')
      .select('id')
      .eq('post_id', postId)
      .eq('user_id', user.id)
      .maybeSingle();
    
    if (likeError) {
      console.error('❌ [fetchUserPostInteractions] Erro ao verificar like:', likeError);
      // Não lançar erro, apenas retornar false
      return { liked: false };
    }

    const liked = !!likeData;
    console.log('✅ [fetchUserPostInteractions] Resultado:', { liked });

    return { liked };
  } catch (error) {
    console.error('❌ [fetchUserPostInteractions] Erro geral:', error);
    return { liked: false };
  }
};

// ===============================
// BUSCAR COMENTÁRIOS DO POST
// ===============================

export const fetchPostComments = async (filters: CommentFilters): Promise<CommentListResponse> => {
  try {
    const { post_id, limit = 10, offset = 0 } = filters;

    console.log('📝 [fetchPostComments] Buscando comentários para post:', post_id);

    // Buscar comentários usando community_post_comments
    const query = supabase
      .from('community_post_comments')
      .select('*')
      .eq('post_id', post_id)
      .order('created_at', { ascending: true });

    const countQuery = supabase
      .from('community_post_comments')
      .select('*', { count: 'exact', head: true })
      .eq('post_id', post_id);

    const [{ data: comments, error }, { count, error: countError }] = await Promise.all([
      query.range(offset, offset + limit - 1),
      countQuery
    ]);

    if (error) {
      console.error('❌ [fetchPostComments] Erro ao buscar comentários:', error);
      throw error;
    }
    if (countError) {
      console.error('❌ [fetchPostComments] Erro ao contar comentários:', countError);
      throw countError;
    }

    console.log('📝 [fetchPostComments] Comentários encontrados:', comments?.length || 0);

    let commentsWithAuthors = comments || [];

    // Buscar dados dos autores se houver comentários
    if (comments && comments.length > 0) {
      const authorIds = [...new Set(comments.map(comment => comment.author_id))];
      console.log('👥 [fetchPostComments] Buscando autores dos comentários:', authorIds);

      const profilePromises = authorIds.map(async (authorId) => {
        try {
          // Buscar dados básicos do profile
          const { data: profileData, error: profileError } = await supabase
            .from('profiles')
            .select('id, nickname, role, slug')
            .eq('id', authorId)
            .single();

          if (profileError) {
            console.warn('⚠️ [fetchPostComments] Erro ao buscar perfil:', {
              authorId,
              error: profileError
            });
            return {
              id: authorId,
              nickname: 'Usuário',
              role: null,
              slug: null,
              avatar_url: null
            };
          }

          // Buscar imagem do usuário baseada no role
          let avatar_url = null;
          
          if (profileData.role === 'mentor') {
            console.log('🖼️ [fetchPostComments] Buscando imagem do mentor...');
            
            const { data: mentorData, error: mentorError } = await supabase
              .from('mentor_info')
              .select('image_url')
              .eq('profile_id', authorId)
              .maybeSingle();
            
            if (!mentorError && mentorData?.image_url) {
              // Processar URL da imagem
              if (mentorData.image_url.startsWith('http')) {
                avatar_url = mentorData.image_url;
              } else {
                // Se é apenas o nome do arquivo, formar caminho completo: {user_id}/{filename}
                let fullPath = mentorData.image_url;
                
                // Se não contém '/', formar o caminho completo
                if (!mentorData.image_url.includes('/')) {
                  fullPath = `${authorId}/${mentorData.image_url}`;
                }
                
                const { data: urlData } = supabase.storage
                  .from('profile-images')
                  .getPublicUrl(fullPath);
                avatar_url = urlData.publicUrl;
              }
              console.log('✅ [fetchPostComments] Imagem do mentor encontrada:', avatar_url);
            }
          } else if (profileData.role === 'jogador') {
            console.log('🖼️ [fetchPostComments] Buscando imagem do jogador...');
            
            const { data: playerData, error: playerError } = await supabase
              .from('player_social_media')
              .select('image_url')
              .eq('profile_id', authorId)
              .order('created_at', { ascending: false })
              .limit(1)
              .maybeSingle();
            
            if (!playerError && playerData?.image_url) {
              // Processar URL da imagem
              if (playerData.image_url.startsWith('http')) {
                avatar_url = playerData.image_url;
              } else {
                // Se é apenas o nome do arquivo, formar caminho completo: {user_id}/{filename}
                let fullPath = playerData.image_url;
                
                // Se não contém '/', formar o caminho completo
                if (!playerData.image_url.includes('/')) {
                  fullPath = `${authorId}/${playerData.image_url}`;
                }
                
                const { data: urlData } = supabase.storage
                  .from('profile-images')
                  .getPublicUrl(fullPath);
                avatar_url = urlData.publicUrl;
              }
              console.log('✅ [fetchPostComments] Imagem do jogador encontrada:', avatar_url);
            }
          }

          // Se não encontrou imagem nas tabelas específicas, tentar buscar no campo image_url da tabela profiles
          if (!avatar_url) {
            console.log('🖼️ [fetchPostComments] Tentando buscar imagem no campo image_url da tabela profiles...');
            
            const { data: profileImageData, error: profileImageError } = await supabase
              .from('profiles')
              .select('image_url')
              .eq('id', authorId)
              .single();
            
            if (!profileImageError && profileImageData?.image_url) {
              avatar_url = profileImageData.image_url;
              console.log('✅ [fetchPostComments] Imagem encontrada na tabela profiles:', avatar_url);
            }
          }

          return {
            ...profileData,
            avatar_url
          };
        } catch (error) {
          console.error(`❌ [fetchPostComments] Erro ao buscar perfil ${authorId}:`, error);
          return {
            id: authorId,
            nickname: 'Usuário',
            role: null,
            slug: null,
            avatar_url: null
          };
        }
      });

      const authors = await Promise.all(profilePromises);

      // Mapear comentários com dados dos autores
      commentsWithAuthors = comments.map(comment => {
        const author = authors.find(a => a.id === comment.author_id);
        
        return {
          id: comment.id,
          post_id: comment.post_id,
          author_id: comment.author_id,
          content: comment.content,
          likes_count: 0, // Comentários não têm likes no sistema atual
          created_at: comment.created_at,
          updated_at: comment.updated_at,
          author: author ? {
            id: author.id,
            nickname: author.nickname,
            avatar_url: author.avatar_url,
            slug: author.slug
          } : undefined
        };
      });
    }

    const total_count = count || 0;
    const has_more = offset + limit < total_count;

    console.log('✅ [fetchPostComments] Comentários processados:', commentsWithAuthors.length);

    return {
      comments: commentsWithAuthors,
      total_count,
      has_more
    };

  } catch (error) {
    console.error('❌ [fetchPostComments] Erro geral:', error);
    throw error;
  }
};

// ===============================
// CRIAR COMENTÁRIO
// ===============================

export const createComment = async (data: CreateCommentData): Promise<CommunityPostComment> => {
  try {
    const { post_id, content } = data;

    console.log('📝 [createComment] Criando comentário:', { post_id, content: content.substring(0, 50) + '...' });

    // Obter o usuário autenticado
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    
    if (authError || !user) {
      console.error('❌ [createComment] Erro de autenticação:', authError);
      throw new Error('Usuário não autenticado');
    }

    const author_id = user.id;

    // Inserir comentário usando community_post_comments
    const { data: newComment, error } = await supabase
      .from('community_post_comments')
      .insert({
        post_id,
        author_id,
        content: content.trim()
      })
      .select('*')
      .single();

    if (error) {
      console.error('❌ [createComment] Erro ao criar comentário:', error);
      throw error;
    }

    console.log('✅ [createComment] Comentário criado:', newComment);

    // Buscar dados do autor para retornar um objeto completo
    const { data: authorProfile, error: authorError } = await supabase
      .from('profiles')
      .select('id, nickname, role, slug')
      .eq('id', author_id)
      .single();

    if (authorError) {
      console.warn('⚠️ [createComment] Erro ao buscar perfil do autor:', authorError);
    }

    // Buscar imagem do usuário baseada no role
    let avatar_url = null;
    
    if (authorProfile?.role === 'mentor') {
      console.log('🖼️ [createComment] Buscando imagem do mentor...');
      
      const { data: mentorData, error: mentorError } = await supabase
        .from('mentor_info')
        .select('image_url')
        .eq('profile_id', author_id)
        .maybeSingle();
      
      if (!mentorError && mentorData?.image_url) {
        // Processar URL da imagem
        if (mentorData.image_url.startsWith('http')) {
          avatar_url = mentorData.image_url;
        } else {
          // Se é apenas o nome do arquivo, formar caminho completo: {user_id}/{filename}
          let fullPath = mentorData.image_url;
          
          // Se não contém '/', formar o caminho completo
          if (!mentorData.image_url.includes('/')) {
            fullPath = `${author_id}/${mentorData.image_url}`;
          }
          
          const { data: urlData } = supabase.storage
            .from('profile-images')
            .getPublicUrl(fullPath);
          avatar_url = urlData.publicUrl;
        }
        console.log('✅ [createComment] Imagem do mentor encontrada:', avatar_url);
      }
    } else if (authorProfile?.role === 'player') {
      console.log('🖼️ [createComment] Buscando imagem do jogador...');
      
      const { data: playerData, error: playerError } = await supabase
        .from('player_social_media')
        .select('image_url')
        .eq('profile_id', author_id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      
      if (!playerError && playerData?.image_url) {
        // Processar URL da imagem
        if (playerData.image_url.startsWith('http')) {
          avatar_url = playerData.image_url;
        } else {
          // Se é apenas o nome do arquivo, formar caminho completo: {user_id}/{filename}
          let fullPath = playerData.image_url;
          
          // Se não contém '/', formar o caminho completo
          if (!playerData.image_url.includes('/')) {
            fullPath = `${author_id}/${playerData.image_url}`;
          }
          
          const { data: urlData } = supabase.storage
            .from('profile-images')
            .getPublicUrl(fullPath);
          avatar_url = urlData.publicUrl;
        }
        console.log('✅ [createComment] Imagem do jogador encontrada:', avatar_url);
      }
    }

    // Se não encontrou imagem nas tabelas específicas, tentar buscar no campo image_url da tabela profiles
    if (!avatar_url) {
      console.log('🖼️ [createComment] Tentando buscar imagem no campo image_url da tabela profiles...');
      
      const { data: profileImageData, error: profileImageError } = await supabase
        .from('profiles')
        .select('image_url')
        .eq('id', author_id)
        .single();
      
      if (!profileImageError && profileImageData?.image_url) {
        avatar_url = profileImageData.image_url;
        console.log('✅ [createComment] Imagem encontrada na tabela profiles:', avatar_url);
      }
    }

    // Transformar para o formato esperado
    const commentResponse: CommunityPostComment = {
      id: newComment.id,
      post_id: newComment.post_id,
      author_id: newComment.author_id,
      content: newComment.content,
      likes_count: 0, // Comentários não têm likes no sistema atual
      created_at: newComment.created_at,
      updated_at: newComment.updated_at,
      author: authorProfile ? {
        id: authorProfile.id,
        nickname: authorProfile.nickname,
        avatar_url: avatar_url,
        slug: authorProfile.slug
      } : undefined
    };

    return commentResponse;
  } catch (error) {
    console.error('❌ [createComment] Erro geral:', error);
    throw error;
  }
};

// ===============================
// ATUALIZAR COMENTÁRIO
// ===============================

export const updateComment = async (commentId: string, data: UpdateCommentData): Promise<CommunityPostComment> => {
  try {
    const { data: comment, error } = await supabase
      .from('community_post_comments')
      .update(data)
      .eq('id', commentId)
      .select(`
        *,
        author:profiles!author_id(id, nickname, avatar_url, slug)
      `)
      .single();

    if (error) throw error;

    return comment as CommunityPostComment;

  } catch (error) {
    console.error('Erro ao atualizar comentário:', error);
    throw new Error('Falha ao atualizar comentário');
  }
};

// ===============================
// DELETAR COMENTÁRIO
// ===============================

export const deleteComment = async (commentId: string, postId: string): Promise<void> => {
  try {
    const { error } = await supabase
      .from('community_post_comments')
      .delete()
      .eq('id', commentId);

    if (error) throw error;

    // Decrementar contador de comentários no post
    const { error: updateError } = await supabase
      .rpc('decrement_post_comments_count', { post_id_param: postId });

    if (updateError) throw updateError;

  } catch (error) {
    console.error('Erro ao deletar comentário:', error);
    throw new Error('Falha ao deletar comentário');
  }
}; 